import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { useOutsideClick, useSelectorActiveChain } from 'components/hooks';
import HeaderMobile from 'components/layout/header/HeaderMobile';
import { useIsMobileBuySellRoute } from 'components/trading/dashboardDetect';
import { redirectTo } from 'lib/redirect';
import { getDashboardUrl } from 'reduxState/markets/listed/listedMarketsUtils';
import { closeMarketsSidebar } from 'reduxState/markets/marketsSidebar';
import { useSelector, staticDispatch } from 'reduxState/store';
import { scrollbarStyle } from 'styleShared';

import MarketsTable from './MarketsTable';

const SidebarWrapper = styled.div<{ $isOpened: boolean; $width: string }>`
  position: absolute;
  left: 4px;

  /** Market header size */
  margin-top: 47px;

  .isMobileStyle & {
    position: fixed;
    top: 0;
    margin-top: 4px;
    overflow-y: scroll;
    ${scrollbarStyle}
  }
  /* Above banners on mobile */
  z-index: 1235000;
  cursor: initial;

  width: ${props => props.$width};
  /* Animation using sliding from left */
  transform: translate(
    -${({ $isOpened, $width }) => ($isOpened ? 0 : $width)},
    0
  );
  .isMobileStyle & {
    width: ${({ $isOpened }) => ($isOpened ? 'calc(100% - 8px)' : '0')};
    height: calc(100vh - 8px);
    transform: translate(-${({ $isOpened }) => ($isOpened ? 0 : '100%')}, 0);
  }
  overflow: hidden;
  ${props => (props.$isOpened ? 'transition: transform 0.4s;' : '')};

  border-radius: 4px;
  background: var(--elevation-light);

  border: ${props =>
    props.$isOpened ? '1px solid var(--mainBorder)' : 'initial'};
  box-shadow: ${props =>
    props.$isOpened ? '10px 10px 34px 4px var(--black)' : 'initial'};
`;

const ContentWrap = styled.div<{ $isOpened: boolean; $width: string }>`
  /* Helps with animation (Prvent text shrink and scroolbar) */
  width: ${props => (props.$isOpened ? props.$width : 0)};
  overflow-x: hidden;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;

  .isMobileStyle & {
    height: 100%;
  }
`;

const InnerScrollableWrap = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  ${scrollbarStyle}
`;

function MarketsTickersScreen() {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isMobile = useSelector(state => state.screen.isMobile);
  const { pathname } = useLocation();
  const panelWidth = isMobile ? '100%' : '600px';

  const opened = useSelector(state => state.markets.marketsSidebar.isOpened);
  const [openedDelayed, setOpenedDelayed] = useState(opened);
  useEffect(() => {
    setTimeout(() => setOpenedDelayed(opened), 100);
  }, [opened]);

  const { activeChain } = useSelectorActiveChain();
  const isTradeRoute = useIsMobileBuySellRoute();

  useEffect(() => {
    return () => {
      if (opened) {
        staticDispatch(closeMarketsSidebar());
      }
    };
  }, [opened]);

  // TODO: might be handy to add keyboard shortcut for toggle eg. ctr + M (Markets)
  // useEffect(() => {
  //   const callback = (event: KeyboardEvent) => {
  //     if ((event.metaKey || event.ctrlKey) && event.code === 'KeyC') {
  //       staticDispatch(toggleMarketsSidebar());
  //     }
  //   };
  //   document.addEventListener('keydown', callback);
  //   return () => {
  //     // Cleanup function to properly remove listener
  //     document.removeEventListener('keydown', callback);
  //   };
  // }, []);

  const handleMarketClick = (marketSymbol: string) => {
    redirectTo(
      isTradeRoute
        ? // Stay in BuySell if open
          getDashboardUrl(activeChain, marketSymbol, undefined, false) +
            '/mobile-trade'
        : getDashboardUrl(activeChain, marketSymbol, undefined, true)
    );

    setTimeout(() => staticDispatch(closeMarketsSidebar()));
  };

  const handleClose = useCallback(() => {
    staticDispatch(closeMarketsSidebar());
  }, []);

  useOutsideClick(wrapperRef, openedDelayed, handleClose);

  return (
    <SidebarWrapper $isOpened={opened} $width={panelWidth} ref={wrapperRef}>
      {opened && (
        <ContentWrap $isOpened={opened} $width={panelWidth}>
          {isMobile && (
            <>
              {pathname === '/markets' ? (
                <HeaderMobile backGoTo="/mobile-more">Markets</HeaderMobile>
              ) : (
                <HeaderMobile onCloseClick={handleClose}>Markets</HeaderMobile>
              )}
            </>
          )}
          <InnerScrollableWrap>
            <MarketsTable onMarketClick={handleMarketClick} isSidebar />
          </InnerScrollableWrap>
        </ContentWrap>
      )}
    </SidebarWrapper>
  );
}

export default MarketsTickersScreen;
